import { Controller } from "stimulus";

// Ticks the creator console from "on air" to "that's a wrap" toward absolute server-rendered
// timestamps, never a client-side "start at 15:00" duration - a premiere's finish has no server
// event at all (LiveEvents::EndPremiere is a cron column flip that broadcasts nothing), so the
// clock is the only thing that can catch it without a new broadcast. A stream's own finish is
// always a full server re-render (the creator's own redirect, or the mark_idle!/mark_live! morph
// refresh in other tabs), so this controller never has to flip a stream's targets itself - only
// tick its countdown and, eventually, redirect.
//
// Two instances mount on one console page (see console.html.erb / _live_dashboard.html.erb): the
// header badge pair and the body panel are DOM siblings (content_for(:header_title) renders into
// the page header, outside <main>), so Stimulus can't scope one controller's targets across both.
// Only the body instance carries a redirect-url value - see the guards in tick() below.
export default class extends Controller {
  static targets = ["live", "finished", "countdown"];
  static values = {
    playbackEndsAtMs: Number,
    roomClosesAtMs: Number,
    serverNowMs: Number,
    redirectUrl: String,
  };

  connect() {
    // Offset a skewed client clock onto server time (minted at render), same correction
    // live-pages--show's controller already uses.
    this.offset = this.serverNowMsValue - Date.now();
    this.redirected = false;

    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnect() {
    if (this.timer) window.clearInterval(this.timer);
  }

  now() {
    return Date.now() + this.offset;
  }

  tick() {
    // No finish known yet (a still-live stream, or a premiere with no synced ends_at) - leave the
    // server-rendered "live" state alone.
    if (!this.playbackEndsAtMsValue) return;

    // classList, not the `hidden` DOM property: every target here is server-rendered with
    // Tailwind's `hidden` utility CLASS (see console.html.erb / _live_dashboard.html.erb), matching
    // live-pages--show's own chatCloseBar toggle - the native `hidden` attribute is a different
    // mechanism from the Tailwind class and setting it here would do nothing to a class-hidden
    // element (and vice versa).
    const finished = this.now() >= this.playbackEndsAtMsValue;
    this.finishedTargets.forEach((el) => { el.classList.toggle("hidden", !finished); });
    this.liveTargets.forEach((el) => { el.classList.toggle("hidden", finished); });

    if (!finished) return;

    const remaining = Math.max(0, this.roomClosesAtMsValue - this.now());
    this.countdownTargets.forEach((el) => { el.textContent = this.format(remaining); });

    if (remaining > 0) return;

    // Redirect guards - all three required:
    // - no roomClosesAtMsValue: never redirect off a missing timestamp (can't happen today,
    //   room_closes_at is always playback_ends_at + AFTERPARTY_LENGTH, but costs nothing to guard).
    // - no redirectUrlValue: the header instance carries none and must never navigate - only the
    //   body instance owns leaving the page. hasRedirectUrlValue would be true even for an empty
    //   attribute, so the header instance must omit it entirely rather than render it blank.
    // - already redirected: a Turbo morph refresh can remount this controller mid-navigation with
    //   a fresh serverNowMs, re-firing tick() before the browser has actually left the page.
    if (!this.roomClosesAtMsValue) return;
    if (!this.hasRedirectUrlValue) return;
    if (this.redirected) return;

    this.redirected = true;
    if (this.timer) window.clearInterval(this.timer);
    window.location.assign(this.redirectUrlValue);
  }

  // M:SS - the after-party never exceeds AFTERPARTY_LENGTH (15 minutes), so no hour-widening.
  format(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }
}
